import { text as textColor } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { Box, Tooltip as MuiTooltip, TooltipProps, Typography, TypographyProps, styled, tooltipClasses } from "@mui/material";
import { FC, ReactNode, useLayoutEffect, useRef, useState } from "react";

export type TEllipsisTextProps = {
  /** Full text shown in the tooltip when the content is truncated. */
  title: string;
  /** Rendered content; defaults to `title`. Use for decorated text (e.g. a bold prefix). */
  children?: ReactNode;
  /** Bound the text width so it can truncate inside auto-layout tables. */
  maxWidth?: number | string;
  placement?: TooltipProps["placement"];
} & Omit<TypographyProps, "title" | "children" | "noWrap">;

/** Shared canvas so text measurement never touches the DOM (no reflow). */
let measureCanvas: HTMLCanvasElement | null = null;
const measureText = (text: string, font: string): number => {
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext("2d");
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

const fontOf = (el: HTMLElement): string => {
  const s = getComputedStyle(el);
  return `${s.fontStyle} ${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
};

/**
 * Single-line text that truncates with a baseline "..." and shows a hover tooltip ONLY when it overflows.
 *
 * The CSS `…` glyph is vertically centered by the font (and `text-overflow` custom strings aren't supported in
 * Firefox), so we never rely on it. We append literal periods — which always sit on the baseline (bottom) — and
 * baseline-align them in every browser:
 *   - plain `title` string: JS-measure the width and cut the string ourselves, then append "...".
 *   - decorated `children`: clip the content (no ellipsis glyph) and render our own "..." as a baseline-aligned sibling.
 */
export const EllipsisText: FC<TEllipsisTextProps> = ({ title, children, maxWidth = "100%", placement = "top", sx, ...rest }) => {
  const decorated = children != null;

  const inner = decorated ? (
    <DecoratedEllipsis maxWidth={maxWidth} sx={sx} onTruncatedTooltip={title} placement={placement} {...rest}>
      {children}
    </DecoratedEllipsis>
  ) : (
    <PlainEllipsis title={title} maxWidth={maxWidth} sx={sx} placement={placement} {...rest} />
  );

  return inner;
};

/** Plain-string path: measure + cut, then append baseline "...". */
const PlainEllipsis: FC<Omit<TEllipsisTextProps, "children">> = ({ title, maxWidth = "100%", placement = "top", sx, ...rest }) => {
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [display, setDisplay] = useState(title);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const recompute = () => {
      const avail = el.clientWidth;
      const font = fontOf(el);
      if (avail === 0 || measureText(title, font) <= avail) {
        setTruncated(false);
        setDisplay(title);
        return;
      }
      const ellipsis = "...";
      const ellipsisW = measureText(ellipsis, font);
      // Binary-search the longest prefix that fits alongside the trailing "...".
      let lo = 0;
      let hi = title.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (measureText(title.slice(0, mid), font) + ellipsisW <= avail) lo = mid;
        else hi = mid - 1;
      }
      setTruncated(true);
      setDisplay(title.slice(0, lo).replace(/\s+$/, "") + ellipsis);
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  return (
    <StyledTooltip title={truncated ? title : ""} placement={placement} arrow>
      <Typography ref={ref} sx={{ display: "block", maxWidth, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", ...sx }} {...rest}>
        {display}
      </Typography>
    </StyledTooltip>
  );
};

/** Decorated path: clip the content and render our own baseline-aligned "..." sibling. */
const DecoratedEllipsis: FC<TypographyProps & { maxWidth?: number | string; onTruncatedTooltip: string; placement?: TooltipProps["placement"] }> = ({
  children,
  maxWidth = "100%",
  onTruncatedTooltip,
  placement = "top",
  sx,
  ...rest
}) => {
  const contentRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const recompute = () => setTruncated(el.scrollWidth > el.clientWidth);
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);

  return (
    <StyledTooltip title={truncated ? onTruncatedTooltip : ""} placement={placement} arrow>
      <Typography sx={{ display: "flex", alignItems: "baseline", maxWidth, minWidth: 0, overflow: "hidden", ...sx }} {...rest}>
        <Box component="span" ref={contentRef} sx={{ minWidth: 0, flexShrink: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "clip" }}>
          {children}
        </Box>
        {truncated && <Box component="span" sx={{ flexShrink: 0 }}>...</Box>}
      </Typography>
    </StyledTooltip>
  );
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: textColor.primary,
    color: "#fff",
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: rounded.sm,
    fontSize: 12,
    lineHeight: 1.5,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: textColor.primary,
  },
}));
